"use client";

/**
 * Generic picker mode for `TokenSelector`. Rendered when `TokenSelector` receives a
 * `dataSource` prop. Encapsulates the popover + command + debounced search + cache +
 * selected-chip logic so the same shell can back any domain-specific picker (coins,
 * prediction markets, etc.) without duplicating the UI.
 *
 * Not a public picker — only `TokenSelector` imports from this file. The public
 * surface is `<TokenSelector dataSource={...} />`. Kept in its own file so
 * `TokenSelector.tsx` stays closer to the CLAUDE.md 1000-LOC soft cap.
 */
import { IconCheckOutline18, IconChevronExpandYOutline18, IconXmarkOutline18 } from "nucleo-ui-outline-18";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PixelLoader } from "@/components/ui/pixel-loader";
import { cn } from "@/lib/utils";
import { useEffect, useMemo, useState, type ReactNode } from "react";

/**
 * Generic data-source contract. When supplied to `<TokenSelector>`, the picker uses
 * these hooks instead of its default Relay + SimDune fetch path — token-specific UI
 * (chain filter, wallet balances, AnySpend CTA, paste-0x fallback) is skipped.
 */
export interface PickerDataSource<T> {
  /** Hook returning items for the debounced search term. Picker owns the debounce. */
  useSearch: (term: string) => {
    items: T[];
    isLoading: boolean;
    error?: Error | null;
  };
  /**
   * Hook to look up a single item by id when rendering a selected chip whose data
   * isn't in the in-memory cache (AI-defaulted values, saved workflow ids, etc.).
   * Required so the call is unconditional — adapters that don't support lookup can
   * supply a no-op: `useLookup: () => ({ item: null })`.
   */
  useLookup: (id: string | undefined) => {
    item: T | null;
  };
}
export interface GenericPickerModeProps {
  label?: string;
  description?: string;
  headerRight?: ReactNode;
  value: string | string[];
  onChange: (value: string | string[]) => void;
  required?: boolean;
  popoverModal?: boolean;
  multiple?: boolean;
  placeholder?: string;
  dataSource?: PickerDataSource<unknown>;
  getKey?: (item: unknown) => string;
  renderRow?: (item: unknown, ctx: {
    selected: boolean;
  }) => ReactNode;
  renderTriggerValue?: (item: unknown | null, raw: string) => ReactNode;
  renderChip?: (args: {
    item: unknown | null;
    id: string;
    onRemove: () => void;
  }) => ReactNode;
  searchPlaceholder?: string;
  emptyLabel?: (term: string) => string;
  errorLabel?: string;
  loadingLabel?: string;
  groupHeading?: (term: string) => string | undefined;
  multiAddMorePlaceholder?: string;
  debounceMs?: number;
  cacheLimit?: number;
}

/**
 * Per-chip wrapper so each `dataSource.useLookup` hook call happens at the top of its
 * own component — required to satisfy hook-ordering rules when a multi-select renders N chips.
 */
function GenericPickerChip({
  id,
  cached,
  useLookup,
  renderChip,
  onRemove
}: {
  id: string;
  cached?: unknown;
  useLookup: (id: string | undefined) => {
    item: unknown | null;
  };
  renderChip: (args: {
    item: unknown | null;
    id: string;
    onRemove: () => void;
  }) => ReactNode;
  onRemove: () => void;
}) {
  // useLookup is always called to keep hook ordering unconditional. The `cached`
  // shortcut passes `undefined` as the id, which adapters should treat as "no lookup".
  const lookup = useLookup(cached ? undefined : id);
  const item = cached ?? lookup.item ?? null;
  return <>{renderChip({
      item,
      id,
      onRemove
    })}</>;
}
export function GenericPickerMode(props: GenericPickerModeProps) {
  const {
    label,
    description,
    headerRight,
    value,
    onChange,
    required = false,
    popoverModal = false,
    multiple = false,
    placeholder,
    dataSource,
    getKey,
    renderRow,
    renderTriggerValue,
    renderChip,
    searchPlaceholder = "Search...",
    emptyLabel = () => "No results.",
    errorLabel = "Lookup failed. Check your connection and try again.",
    loadingLabel = "Searching...",
    groupHeading = () => undefined,
    multiAddMorePlaceholder = "Add more...",
    debounceMs = 300,
    cacheLimit = 200
  } = props;

  // These are guaranteed by the TokenSelector wrapper — it only enters this mode when
  // dataSource is provided, and callers must also supply the renderer props. Narrow
  // here so the body can treat them as required.
  if (!dataSource || !getKey || !renderRow || !renderTriggerValue) {
    throw new Error("TokenSelector: `dataSource` requires `getKey`, `renderRow`, and `renderTriggerValue` to be provided.");
  }
  const [containerEl, setContainerEl] = useState<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedTerm, setDebouncedTerm] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedTerm(searchTerm), debounceMs);
    return () => clearTimeout(t);
  }, [searchTerm, debounceMs]);
  const {
    items,
    isLoading,
    error
  } = dataSource.useSearch(debouncedTerm);

  // Cache items on search so chips/trigger render instantly without a second lookup.
  // Map preserves insertion order for eviction. Cap prevents unbounded growth in long sessions.
  const [cache, setCache] = useState<Map<string, unknown>>(() => new Map());
  useEffect(() => {
    if (items.length === 0) return;
    setCache(prev => {
      let next = prev;
      for (const item of items) {
        const key = getKey(item);
        if (!next.has(key)) {
          if (next === prev) next = new Map(prev);
          next.set(key, item);
        }
      }
      if (next !== prev && next.size > cacheLimit) {
        const iter = next.keys();
        const dropCount = next.size - cacheLimit;
        for (let i = 0; i < dropCount; i++) next.delete(iter.next().value as string);
      }
      return next;
    });
  }, [items, getKey, cacheLimit]);
  const selectedIds: string[] = useMemo(() => {
    if (multiple) return Array.isArray(value) ? value : value ? [value as string] : [];
    return [];
  }, [value, multiple]);
  const singleValue = !multiple && typeof value === "string" ? value : "";
  const cachedSelected = !multiple && singleValue ? cache.get(singleValue) : undefined;
  // useLookup is always called (interface makes it required) to keep hook ordering
  // unconditional. When the cache is warm or there's no single value, we pass
  // `undefined` as the id — adapters should treat that as "no lookup".
  const lookupNeeded = !multiple && singleValue && !cachedSelected;
  const lookup = dataSource.useLookup(lookupNeeded ? singleValue : undefined);
  const selectedItem = cachedSelected ?? lookup.item ?? null;
  const filteredItems = multiple ? items.filter(item_0 => !selectedIds.includes(getKey(item_0))) : items;
  const handleSelect = (item_1: unknown) => {
    const id = getKey(item_1);
    setCache(prev_0 => {
      if (prev_0.has(id)) return prev_0;
      const next_0 = new Map(prev_0);
      next_0.set(id, item_1);
      return next_0;
    });
    if (multiple) {
      if (!selectedIds.includes(id)) onChange([...selectedIds, id]);
      setSearchTerm("");
    } else {
      onChange(id);
      setSearchTerm("");
      setOpen(false);
    }
  };
  const handleRemove = (id_0: string) => {
    if (!multiple) return;
    onChange(selectedIds.filter(x => x !== id_0));
  };
  return <div ref={setContainerEl} className="space-y-2">
      {label && <div className="flex items-center justify-between gap-3">
          <label className="field-label block">
            {label}
            {required && <span className="ml-1 text-red-500">*</span>}
          </label>
          {headerRight}
        </div>}
      {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}

      {multiple && selectedIds.length > 0 && renderChip && <div className="flex flex-wrap gap-2">
          {selectedIds.map(id_1 => <GenericPickerChip key={id_1} id={id_1} cached={cache.get(id_1)} useLookup={dataSource.useLookup} renderChip={renderChip} onRemove={() => handleRemove(id_1)} />)}
        </div>}

      <Popover open={open} onOpenChange={next_1 => {
      setOpen(next_1);
      if (!next_1) setSearchTerm("");
    }} modal={popoverModal}>
        <PopoverTrigger asChild>
          <button type="button" role="combobox" aria-expanded={open} className="border-ds-neutral-300 text-ds-text-primary dark:border-ds-neutral-700 bg-ds-surface-card flex w-full items-center justify-between rounded-lg border px-3 py-2 text-left text-sm focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20">
            {!multiple && singleValue ? <span className="flex min-w-0 items-center gap-2">{renderTriggerValue(selectedItem, singleValue)}</span> : multiple && selectedIds.length > 0 ? <span className="text-ds-text-subtle">{multiAddMorePlaceholder}</span> : <span className="text-ds-text-disabled dark:text-ds-text-muted">{placeholder}</span>}
            <span className="ml-2 flex shrink-0 items-center gap-1">
              {!multiple && singleValue && !required && <span role="button" tabIndex={0} aria-label="Clear selection" onClick={e => {
              e.stopPropagation();
              onChange("");
              setSearchTerm("");
              setOpen(false);
            }} onKeyDown={e_0 => {
              if (e_0.key === "Enter" || e_0.key === " ") {
                e_0.preventDefault();
                e_0.stopPropagation();
                onChange("");
                setSearchTerm("");
                setOpen(false);
              }
            }} className="text-ds-text-disabled hover:bg-ds-neutral-200 hover:text-ds-text-subtle dark:hover:bg-ds-neutral-700 inline-flex cursor-pointer items-center rounded-full p-0.5 transition-colors dark:hover:text-zinc-300">
                  <IconXmarkOutline18 className="h-3 w-3" />
                </span>}
              <IconChevronExpandYOutline18 className="h-4 w-4 opacity-50" />
            </span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="bg-ds-surface-card !z-[100] w-[var(--radix-popover-trigger-width)] p-0" align="start" side="bottom" container={popoverModal ? containerEl ?? undefined : undefined}>
          <Command shouldFilter={false}>
            <CommandInput placeholder={searchPlaceholder} value={searchTerm} onValueChange={setSearchTerm} autoFocus />
            <CommandList className="max-h-72">
              {isLoading ? <div className="body-sm flex items-center justify-center gap-2 py-6">
                  <PixelLoader size="xs" />
                  <span>{loadingLabel}</span>
                </div> : error ? <div className="body-sm text-ds-text-danger py-6 text-center">{errorLabel}</div> : <>
                  <CommandEmpty>{emptyLabel(searchTerm)}</CommandEmpty>
                  {filteredItems.length > 0 && <CommandGroup heading={groupHeading(searchTerm)}>
                      {filteredItems.map(item_2 => {
                  const key_0 = getKey(item_2);
                  const selected = multiple ? selectedIds.includes(key_0) : singleValue === key_0;
                  return <CommandItem key={key_0} value={key_0} onSelect={() => handleSelect(item_2)} className="hover:bg-ds-surface-hover dark:hover:bg-ds-neutral-800">
                            <div className="flex w-full cursor-pointer items-center gap-2">
                              <IconCheckOutline18 className={cn("h-4 w-4 flex-shrink-0", selected ? "opacity-100" : "opacity-0")} />
                              {renderRow(item_2, {
                        selected
                      })}
                            </div>
                          </CommandItem>;
                })}
                    </CommandGroup>}
                </>}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>;
}